/**
 * OpenTelemetry metrics for @spatius/avatarkit-rtc SDK.
 *
 * Piggybacks on the global MeterProvider that @spatius/avatarkit registers via
 * `metrics.setGlobalMeterProvider()`, exactly as otel-tracker.ts does for logs.
 * We own no exporter, reader, or backend config — avatarkit owns all of it
 * (OTLP HTTP → OpenObserve, DELTA temporality, 10s export interval), and its
 * Resource carries app_id / region / dsm / sdk.version, so metrics recorded here
 * inherit those attributes without this SDK having to know them.
 *
 * The meter name distinguishes RTC series from web-sdk's ('spatius-avatarkit').
 *
 * If avatarkit has not initialized OTel yet, getMeter() resolves to a no-op and
 * recordings are dropped — same accepted trade-off as the logs and PostHog paths.
 *
 * @packageDocumentation
 * @internal
 */

import { metrics } from '@opentelemetry/api';
import type { Attributes } from '@opentelemetry/api';
import { recordMetric as avatarkitRecordMetric } from '@spatius/avatarkit/internal-telemetry';
import { recordTelemetry } from './telemetry-recorder';

/**
 * Kept as a no-op: readings are now recorded through the host SDK, which owns
 * the meter and stamps its own instrumentation-scope version. The setter stays
 * so telemetry.ts keeps one shape for all three channels.
 * @internal
 */
export function setMetricsVersion(_version: string): void {}

/**
 * Force-export any buffered metric readings.
 *
 * avatarkit owns the MeterProvider and its periodic reader, so `forceFlush` is
 * not guaranteed to be present and is called defensively; the global registry
 * may also hand back a proxy wrapping the real provider.
 * @internal
 */
export function flushMetrics(): void {
  try {
    type FlushableProvider = {
      forceFlush?: () => Promise<void>;
      getDelegate?: () => FlushableProvider;
    };
    const registered = metrics.getMeterProvider() as FlushableProvider;
    const provider = registered.getDelegate?.() ?? registered;
    provider.forceFlush?.().catch(() => {});
  } catch {
    // ignore
  }
}

/**
 * Record one value on a histogram.
 *
 * Values must be finite integers: the backing store cannot represent
 * non-integer values, which is why ratios are reported as integer percentages
 * (or as separate numerator/denominator) rather than fractions — the same
 * constraint web-sdk works under.
 *
 * Labels must be low cardinality. High-cardinality identifiers (conversation_id,
 * seq numbers) belong on the event/log record, never here: each distinct label
 * value multiplies the number of stored time series.
 *
 * @param name - metric name, snake_case, unit-suffixed (`_ms`, `_count`)
 * @param value - finite number; non-finite values are dropped
 * @param labels - low-cardinality dimensions
 * @internal
 */
export function recordMetric(
  name: string,
  value: number,
  labels: Attributes = {},
): void {
  if (typeof window === 'undefined') return;
  if (!Number.isFinite(value)) return;

  // Recorded as the histogram receives them, so a label meant to stay a number
  // is visibly still a number here.
  recordTelemetry('metric', name, { value, labels });

  try {
    // Through avatarkit rather than the global meter directly: a metrics export
    // is skipped entirely unless that round carried real business data, and the
    // flag saying so lives inside avatarkit. Writing to the same instrument via
    // the global provider leaves that flag clear, so the batch — these readings
    // included — is dropped before it is sent.
    avatarkitRecordMetric(name, value, labels);
  } catch {
    // Never let telemetry break the caller.
  }
}
